import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleModal from "@/components/feature/ArticleModal";
import usePageMeta from "@/hooks/usePageMeta";
import { films } from "@/mocks/films";
import { articles } from "@/mocks/reviews";
import type { Article } from "@/types/film";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import QuickEntries from "@/pages/home/components/QuickEntries";
import LatestSection from "@/pages/home/components/LatestSection";
import HotRanking from "@/pages/home/components/HotRanking";
import ReviewHighlights from "@/pages/home/components/ReviewHighlights";

const features = [
  {
    icon: "ri-book-open-line",
    title: "剧情梳理",
    description: "按时间线还原故事，标出每一个关键选择发生的时刻。",
  },
  {
    icon: "ri-user-heart-line",
    title: "角色分析",
    description: "拆解人物的动机与局限，理解他们为什么只能这样选择。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "影评解读",
    description: "从叙事、镜头与伦理立场三个角度，给出独立的分析。",
  },
];

export default function Home() {
  const [activeArticle, setActiveArticle] = useState<Article | null>(null);

  usePageMeta(
    "伦理视频—精选伦理类影视作品在线观看与介绍",
    "伦理视频为您提供丰富的伦理题材电影和电视剧资源，涵盖剧情介绍、角色分析及影评，助您全面了解伦理类影视内容。",
    "伦理视频,伦理电影,伦理电视剧,伦理影视,伦理剧情,影视介绍,影视分析",
  );

  const featured = useMemo(() => films.filter((film) => film.featured).slice(0, 5), []);
  const latest = useMemo(
    () => [...films].sort((a, b) => b.year - a.year || b.rating - a.rating).slice(0, 5),
    [],
  );
  const hot = useMemo(() => [...films].sort((a, b) => b.views - a.views).slice(0, 6), []);
  const highlightArticles = useMemo(() => articles.slice(0, 3), []);

  return (
    <SiteLayout overlay>
      <HeroCarousel films={featured} />
      <QuickEntries />
      <LatestSection films={latest} />
      <HotRanking films={hot} />

      <section className="w-full px-4 py-12 md:px-10 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <span className="font-label text-[11px] uppercase tracking-[0.3em] text-accent-600">About Us</span>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
              我们如何理解伦理题材
            </h2>
            <div className="mt-4 space-y-3 text-sm leading-[1.9] text-foreground-700">
              <p>
                伦理影视最迷人的地方，是它从不提供简单的答案。一个决定背后，往往同时站着责任、恐惧、爱与自私。伦理视频试图做的，是把这些纠缠在一起的东西一件件摊开。
              </p>
              <p>
                我们不写剧情复述式的简介，也不做打分式的推荐。每一部作品都会配上完整的剧情梳理、角色动机分析与独立影评，让观众在观看之后，还能继续想一会儿。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/about"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                了解伦理视频
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/contact"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                给我们留言
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`flex gap-4 py-4 ${index > 0 ? "border-t border-background-200" : "pt-0"} ${
                    index === features.length - 1 ? "pb-0" : ""
                  }`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${feature.icon} text-lg`} />
                  </span>
                  <div>
                    <h3 className="font-label text-sm font-medium text-foreground-950">{feature.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ReviewHighlights articles={highlightArticles} onOpen={setActiveArticle} />
      <ArticleModal article={activeArticle} onClose={() => setActiveArticle(null)} />
    </SiteLayout>
  );
}